/**
 * QA reviews, and how far the grader can be trusted.
 *
 * Two different things live in one table and the screen keeps them apart,
 * because conflating them is how a system convinces itself it is working:
 *
 *  - a **human** verdict, recorded when somebody agreed or disagreed with a
 *    draft in the ticket workspace;
 *  - a **grader** score, written by the daily job;
 *  - and where both exist for one ticket, a **calibration pair** — the only
 *    row that says anything about whether the grader is any good.
 *
 * The calibration panel leads with the disagreements that matter. A human
 * failing something the grader passed means every pass rate we publish is
 * inflated, and that sentence belongs above the headline figure rather than
 * below it.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Play, TriangleAlert } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { formatPercent, formatRelative } from '../lib/format';

interface Review {
  id: string;
  ticketId: string;
  ticketRef: string;
  brand: string;
  intent: string | null;
  score: number | null;
  humanScore: number | null;
  issue: string | null;
  reviewedBy: string | null;
  createdAt: string;
  kind: 'grader' | 'human' | 'calibration_pair';
}

interface Summary {
  total: number;
  graded: number;
  human: number;
  calibrationPairs: number;
}

interface Calibration {
  calibration: {
    sampleSize: number;
    agreement: number | null;
    kappa: number | null;
    falseAlarms: number;
    missedFailures: number;
    missedFailureIds: string[];
    sufficient: boolean;
  };
  summary: string;
  /** Labels whose vendor reply has not been graded yet. */
  ungraded: number;
  matchable: number;
  /** What Grade next 100 will actually work through. */
  gradeable: number;
  /** Verdicts imported without a ticket reference — repairable by re-import. */
  noTicketRef: number;
  noTicketRefBySheet: Array<{ sheet: string; count: number }>;
  /** Tickets we tried and could not read, or with no public reply to grade. */
  skippedUnreadable: number;
  /** Pre-filled dropdown rows imported as verdicts — deletable, not data. */
  phantom: number;
  /**
   * The disagreements that matter, with the auditor's own words beside ours.
   *
   * A count says the grader is blind. Only the note says to WHAT, and that is
   * the sentence somebody can act on.
   */
  /** Misses where the judge read the auditor's criterion and still passed. */
  missedWithNote: number;
  /** Misses with no criterion written down — nothing to check against. */
  missedWithoutNote: number;
  missedFailureDetail: Array<{
    conversationId: string;
    humanSaid: string | null;
    /** The auditor's context column — never a criterion. */
    otherNotes: string | null;
    weSaid: string | null;
  }>;
}

const KIND_LABEL: Record<Review['kind'], { label: string; tone: 'fact' | 'held' | 'neutral' }> = {
  calibration_pair: { label: 'both', tone: 'fact' },
  human: { label: 'human', tone: 'held' },
  grader: { label: 'grader', tone: 'neutral' },
};

export function QaReviewsPage() {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['qa', 'summary'],
    queryFn: () => apiGet<Summary>('/api/qa/summary'),
  });
  const reviews = useQuery({
    queryKey: ['qa', 'reviews'],
    queryFn: () => apiGet<Review[]>('/api/qa/reviews'),
  });
  const calibration = useQuery({
    queryKey: ['qa', 'calibration'],
    queryFn: () => apiGet<Calibration>('/api/evals/calibration'),
  });

  const run = useMutation({
    mutationFn: () =>
      apiPost<{ scored: number; candidates: number; skipped: number; reason?: string }>(
        '/api/qa/run',
      ),
    onSuccess: async (result) => {
      setOutcome(
        result.reason ??
          `Scored ${result.scored} of ${result.candidates} run(s)${result.skipped > 0 ? `, ${result.skipped} not selected` : ''}.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['qa'] });
    },
    onError: (error: unknown) =>
      setOutcome(error instanceof ApiError ? error.message : 'The QA pass did not run.'),
  });

  if (reviews.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-5">
      <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
        <Tile label="Reviews" value={String(summary.data?.total ?? 0)} note="all time" />
        <Tile label="From the grader" value={String(summary.data?.graded ?? 0)} note="daily QA pass" />
        <Tile label="From a person" value={String(summary.data?.human ?? 0)} note="agree / disagree" />
        <Tile
          label="Calibration pairs"
          value={String(summary.data?.calibrationPairs ?? 0)}
          note="both scores present"
        />
      </div>

      <CalibrationPanel data={calibration.data} />

      <Card className="mt-4">
        <CardHeader
          title="Reviews"
          meta={reviews.data?.length ? `${reviews.data.length} newest` : undefined}
          action={
            <Button size="sm" disabled={run.isPending} onClick={() => run.mutate()}>
              <Play size={13} strokeWidth={1.75} aria-hidden />
              {run.isPending ? 'Scoring…' : "Score yesterday's tickets"}
            </Button>
          }
        />

        {outcome ? <p className="px-4 pt-3 text-xs text-ink-700">{outcome}</p> : null}

        {(reviews.data ?? []).length === 0 ? (
          <div className="px-4 py-12 text-center">
            <ClipboardCheck size={28} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
            <p className="text-sm text-ink-700">Nothing reviewed yet.</p>
            <p className="mx-auto mt-2 max-w-[540px] text-sm text-ink-400">
              Two things fill this: a person agreeing or disagreeing with a draft in the ticket
              workspace, and the daily pass scoring yesterday&rsquo;s tickets. The daily pass needs
              a judge model with a working key — without one there is nothing on a live ticket it
              can score.
            </p>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Ticket</Th>
                <Th>Brand</Th>
                <Th>Intent</Th>
                <Th className="text-right">Grader</Th>
                <Th className="text-right">Human</Th>
                <Th>Source</Th>
                <Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {(reviews.data ?? []).map((review) => (
                <tr key={review.id}>
                  <Td>
                    <Link
                      to={`/tickets/${review.ticketId}`}
                      className="font-mono text-xs text-accent-700 hover:underline"
                    >
                      #{review.ticketRef}
                    </Link>
                    {review.issue ? (
                      <div className="max-w-[280px] truncate text-2xs text-ink-400" title={review.issue}>
                        {review.issue}
                      </div>
                    ) : null}
                  </Td>
                  <Td className="text-xs text-ink-500">{review.brand}</Td>
                  <Td className="text-xs text-ink-500">
                    {review.intent?.replace(/_/g, ' ') ?? '—'}
                  </Td>
                  <Td className="tabular text-right text-ink-700">
                    {review.score === null ? (
                      <span className="text-ink-300">—</span>
                    ) : (
                      review.score.toFixed(2)
                    )}
                  </Td>
                  <Td className="tabular text-right text-ink-700">
                    {review.humanScore === null ? (
                      <span className="text-ink-300">—</span>
                    ) : review.humanScore >= 0.5 ? (
                      <span className="text-ok-700">agreed</span>
                    ) : (
                      <span className="text-stop-700">disagreed</span>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={KIND_LABEL[review.kind].tone}>{KIND_LABEL[review.kind].label}</Badge>
                    {review.reviewedBy ? (
                      <div className="text-2xs text-ink-400">{review.reviewedBy}</div>
                    ) : null}
                  </Td>
                  <Td className="text-xs text-ink-500">{formatRelative(review.createdAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function CalibrationPanel({ data }: { data: Calibration | undefined }) {
  const queryClient = useQueryClient();
  const [gradeOutcome, setGradeOutcome] = useState<string | null>(null);

  const gradeBatch = useMutation({
    mutationFn: () =>
      apiPost<{ graded: number; skipped: Array<{ conversationId: string; reason: string }> }>(
        '/api/evals/calibration/grade?limit=100',
      ),
    onSuccess: async (result) => {
      // The first skip reason rides along because it is usually THE reason —
      // a provider outage skips the whole batch with one message, and a
      // count alone reads as "some dead tickets" when it is "the judge is
      // down".
      const firstReason = result.skipped[0]?.reason;
      setGradeOutcome(
        `Graded ${result.graded} label(s)` +
          (result.skipped.length > 0
            ? `, ${result.skipped.length} skipped${firstReason ? ` (first: ${firstReason})` : ''}`
            : '') +
          '.',
      );
      await queryClient.invalidateQueries({ queryKey: ['qa'] });
    },
    onError: async (error: unknown) => {
      // A 100-label batch can outlive the proxy while the server finishes.
      // Every label is written as it lands, so nothing is lost — a client
      // error here (not a server refusal) means "still going", not "failed".
      setGradeOutcome(
        error instanceof ApiError
          ? error.message
          : 'The batch is taking a while — it keeps running on the server and each label is saved as it lands. Refresh this page in a couple of minutes.',
      );
      await queryClient.invalidateQueries({ queryKey: ['qa'] });
    },
  });

  const regrade = useMutation({
    mutationFn: () => apiPost<{ cleared: number }>('/api/evals/calibration/reset'),
    onSuccess: async (result) => {
      setGradeOutcome(
        `${result.cleared} verdict(s) cleared — Grade next 100 now re-scores them with the current grader.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['qa'] });
    },
    onError: (error: unknown) =>
      setGradeOutcome(error instanceof ApiError ? error.message : 'The reset did not run.'),
  });

  if (!data) return null;
  const c = data.calibration;
  const gradeButton = (
    <div className="flex items-center gap-2">
      {/* Shown only once something IS graded: a calibration measures the
          grader, and when the grader changes the old verdicts describe a
          grader that no longer exists. */}
      {c.sampleSize > 0 ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={regrade.isPending || gradeBatch.isPending}
          onClick={() => regrade.mutate()}
          title="Clears every machine verdict so the next grading passes re-score them with the current judge. Use after the grader itself changes."
        >
          {regrade.isPending ? 'Clearing…' : 'Regrade (judge changed)'}
        </Button>
      ) : null}
      {data.gradeable > 0 ? (
        <Button size="sm" disabled={gradeBatch.isPending} onClick={() => gradeBatch.mutate()}>
          <Play size={13} strokeWidth={1.75} aria-hidden />
          {gradeBatch.isPending ? 'Grading…' : 'Grade next 100'}
        </Button>
      ) : null}
    </div>
  );

  return (
    <Card>
      <CardHeader
        title="Is the grader any good?"
        meta="our verdict against a human's, on the same vendor reply"
        action={data.gradeable > 0 || c.sampleSize > 0 ? gradeButton : undefined}
      />

      {gradeOutcome ? <p className="px-4 pt-3 text-xs text-ink-700">{gradeOutcome}</p> : null}

      {c.sampleSize === 0 ? (
        <div className="px-4 py-6">
          <p className="text-sm text-ink-700">Uncalibrated.</p>
          <p className="mt-1.5 max-w-[640px] text-sm text-ink-400">
            {data.matchable > 0
              ? `${data.matchable} human label(s) are loaded, and none of the replies they judged has been graded yet. Grade next 100 reads each vendor reply off its Zendesk ticket and scores the same text the auditor scored — each batch is up to 100 Zendesk reads and 100 judge calls, and can take a few minutes.`
              : 'No human labels are loaded. Import the ops sheet — the Pass/Fail column is the calibration set.'}
          </p>
          <QueueGaps data={data} />
        </div>
      ) : (
        <div className="px-4 py-4">
          {/* The dangerous direction, above the headline figure. */}
          {c.missedFailures > 0 ? (
            <div className="mb-3 flex items-start gap-2.5 rounded border border-line bg-stop-100 px-3 py-2.5">
              <TriangleAlert size={15} strokeWidth={1.75} className="mt-px shrink-0 text-stop-700" aria-hidden />
              <div className="text-xs text-stop-700">
                <span className="font-semibold">
                  {c.missedFailures} ticket(s) a human failed and the grader passed.
                </span>{' '}
                Every pass rate we publish is inflated by exactly these.{' '}
                {c.missedFailureIds.length > 0 ? (
                  <span className="font-mono">{c.missedFailureIds.slice(0, 8).join(', ')}</span>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
            <Tile label="Agreement" value={formatPercent(c.agreement, 0)} note={`${c.sampleSize} labels`} />
            <Tile
              label="Kappa"
              value={c.kappa === null ? '—' : c.kappa.toFixed(2)}
              // The number that stops raw agreement flattering us.
              note="agreement above chance"
            />
            <Tile label="Missed failures" value={String(c.missedFailures)} note="grader too lenient" />
            <Tile label="False alarms" value={String(c.falseAlarms)} note="grader too harsh" />
          </div>

          <p className="mt-3 text-xs text-ink-500">{data.summary}</p>

          {data.gradeable > 0 ? (
            <p className="mt-1.5 text-xs text-ink-400">
              {data.gradeable} gradeable label(s) remain — Grade next 100 works through them. A
              calibration over {c.sampleSize} labels is a different claim from one over{' '}
              {data.matchable}.
            </p>
          ) : null}

          <QueueGaps data={data} />

          {/*
            The list, not the count. "The grader missed 30" tells somebody it is
            blind; only the auditor's own words say to what, and that sentence
            is the one that turns a calibration into a change.
          */}
          {data.missedFailureDetail.length > 0 ? (
            <div className="mt-4 border-t border-line pt-3">
              <p className="eyebrow mb-2 text-ink-500">What the grader is blind to</p>
              {/* The split that decides what a miss MEANS. With a note the
                  judge read the auditor's criterion and still passed — a judge
                  failure. Without one there was no sentence to check against —
                  a sheet gap, not a grader one. */}
              <p className="mb-2 max-w-[640px] text-xs text-ink-400">
                {data.missedWithNote} of {c.missedFailures} carry an auditor note the judge read
                and still passed — those are judge failures, listed first.{' '}
                {data.missedWithoutNote} have no note in the graded column: their failure reason
                was never written as a criterion, so only the rules and tone checks could ever
                catch them.
              </p>
              <ul className="space-y-2.5">
                {data.missedFailureDetail.map((miss) => (
                  <li key={miss.conversationId} className="text-xs">
                    <span className="font-mono text-ink-500">{miss.conversationId}</span>
                    <p className="mt-0.5 text-ink-900">{miss.humanSaid ?? '(no note)'}</p>
                    {!miss.humanSaid && miss.otherNotes ? (
                      <p className="mt-0.5 text-ink-500">
                        other notes (not a criterion): {miss.otherNotes}
                      </p>
                    ) : null}
                    <p className="mt-0.5 text-ink-400">we said: {miss.weSaid ?? '—'}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/**
 * The labels the grade button will NEVER reach, and why.
 *
 * Without this the panel folded them into "not graded yet", which turned a
 * dead queue into an infinite one: 920 labels imported without a ticket
 * reference sat behind a button that re-skipped the same eight unreadable
 * tickets and reported "graded 0" every click.
 */
function QueueGaps({ data }: { data: Calibration }) {
  const queryClient = useQueryClient();
  const [purgeOutcome, setPurgeOutcome] = useState<string | null>(null);
  const purge = useMutation({
    mutationFn: () => apiPost<{ deleted: number }>('/api/evals/audit-labels/purge-phantoms'),
    onSuccess: async (result) => {
      setPurgeOutcome(`${result.deleted} template row(s) removed.`);
      await queryClient.invalidateQueries({ queryKey: ['qa'] });
    },
    onError: (error: unknown) =>
      setPurgeOutcome(error instanceof ApiError ? error.message : 'The removal did not run.'),
  });

  if (data.noTicketRef === 0 && data.skippedUnreadable === 0 && data.phantom === 0) return null;
  return (
    <div className="mt-3 max-w-[640px] space-y-1.5 text-xs text-ink-400">
      {data.phantom > 0 ? (
        <p>
          <span className="font-semibold text-ink-700">
            {data.phantom} row(s) carry a verdict and nothing else
          </span>{' '}
          — the workbook pre-fills the Status dropdown down the whole column, so untouched
          template rows imported as failures nobody judged. They distort the human pass rate and
          are safe to delete: any row a person touched in any way stays.{' '}
          <button
            type="button"
            className="font-medium text-accent-700 hover:underline disabled:opacity-45"
            disabled={purge.isPending}
            onClick={() => purge.mutate()}
          >
            {purge.isPending ? 'Removing…' : `Remove ${data.phantom} template row(s)`}
          </button>
        </p>
      ) : null}
      {purgeOutcome ? <p className="text-ink-700">{purgeOutcome}</p> : null}
      {data.noTicketRef > 0 ? (
        <p>
          <span className="font-semibold text-ink-700">
            {data.noTicketRef} label(s) imported without a ticket reference
          </span>{' '}
          — their sheet&rsquo;s link column wasn&rsquo;t recognised, so their verdicts cannot be
          tied to a ticket. The importer now also finds ticket links by shape: re-import the same
          workbook and these repair in place. Grading already done, activated cases and enriched
          drafts are not touched by a re-import.
          {data.noTicketRefBySheet.length > 0 ? (
            <span className="mt-0.5 block font-mono text-2xs">
              {data.noTicketRefBySheet
                .map(({ sheet, count }) => `${sheet}: ${count}`)
                .join(' · ')}
            </span>
          ) : null}
        </p>
      ) : null}
      {data.skippedUnreadable > 0 ? (
        <p>
          {data.skippedUnreadable} label(s) skipped for good: their tickets could not be read in
          Zendesk (deleted or merged), or carry no public agent reply to grade.
        </p>
      ) : null}
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-lg bg-surface px-4 py-3 shadow-hairline">
      <div className="eyebrow">{label}</div>
      <div className="tabular mt-1 text-2xl font-semibold text-ink-900">{value}</div>
      <div className="text-2xs text-ink-400">{note}</div>
    </div>
  );
}
